import { useMemo, useState } from 'react';
import { RISK_QUICK, WIN_QUICK, RR_QUICK } from '@/mocks/risk';
import { formatVN, formatSigned, Label, NumberInput, QuickChips, Stat, Slider, NextStep } from '../ui';

const RUIN_ACCENT = '#f59e0b';

const WHEN_CARDS = [
  {
    title: 'Đánh giá chiến lược',
    desc: 'Kiểm tra xem chiến lược có đủ lợi thế (edge) để tồn tại lâu dài hay không, dựa trên tỷ lệ thắng và tỷ lệ lời/lỗ.',
    example: 'Thắng 55%, lời/lỗ 1:2 → kỳ vọng mỗi lệnh +0,65R.',
  },
  {
    title: 'Nhận diện rủi ro ẩn',
    desc: 'Phát hiện khi tỷ lệ thắng và lời/lỗ chưa đủ để tránh phá sản — dù thắng thua xen kẽ, tài khoản vẫn có thể về 0.',
    example: 'Kỳ vọng ≤ 0 → xác suất phá sản tiến tới 100%.',
  },
  {
    title: 'Điều chỉnh rủi ro',
    desc: 'Giảm mức rủi ro mỗi lệnh để kéo dài tuổi thọ tài khoản và giữ nguy cơ phá sản ở mức thấp.',
    example: 'Giảm rủi ro từ 5% xuống 1% mỗi lệnh → đơn vị rủi ro tăng từ 20 lên 100, giảm mạnh xác suất phá sản.',
  },
];

export default function RuinTab({ onNext }: { onNext: () => void }) {
  const [winRate, setWinRate] = useState(55);
  const [rewardRatio, setRewardRatio] = useState(2);
  const [riskPerTrade, setRiskPerTrade] = useState(1);

  const { edge, units, ruinPct } = useMemo(() => {
    const p = winRate / 100;
    const q = 1 - p;
    const e = p * rewardRatio - q;
    const u = riskPerTrade > 0 ? 100 / riskPerTrade : 0;
    let r = 100;
    if (e > 0 && u > 0) {
      const base = (1 - e) / (1 + e);
      if (base <= 0) r = 0;
      else r = Math.pow(base, u) * 100;
    }
    return { edge: e, units: u, ruinPct: r };
  }, [winRate, rewardRatio, riskPerTrade]);

  const level = ruinPct >= 50 ? 'Rất cao' : ruinPct >= 25 ? 'Cao' : ruinPct >= 10 ? 'Trung bình' : ruinPct >= 1 ? 'Thấp' : 'Rất thấp';
  const levelColor = ruinPct >= 50 ? '#f43f5e' : ruinPct >= 10 ? '#f59e0b' : '#10b981';
  const displayRuin = ruinPct > 0 && ruinPct < 0.01 ? '< 0,01' : formatVN(ruinPct, 2, 2);

  return (
    <div className="space-y-6">
      {/* Disclaimer */}
      <div className="flex items-start gap-3 rounded-2xl border border-amber-100 bg-amber-50/60 p-4">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
          <i className="ri-information-line text-lg" />
        </span>
        <p className="text-sm leading-relaxed text-slate-600">
          Đây là mô hình xác suất toán học ước tính nguy cơ tài khoản về 0 trong dài hạn, dựa trên giả định tỷ lệ thắng và tỷ lệ lời/lỗ ổn định. Kết quả không phải là dự báo chính xác cho từng tài khoản cụ thể.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Left: inputs */}
        <div className="lg:col-span-3">
          <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
            <div className="mb-6 flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                <i className="ri-alert-line text-lg" />
              </span>
              <div>
                <h3 className="font-display text-lg font-bold leading-tight text-slate-900">Thông số chiến lược</h3>
                <p className="text-xs text-slate-400">Tỷ lệ thắng, tỷ lệ lời/lỗ và rủi ro mỗi lệnh</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label hint={`${formatVN(winRate, 0, 0)}%`}>Tỷ lệ thắng (Win rate)</Label>
                <Slider value={winRate} onChange={setWinRate} min={30} max={80} step={1} accent={RUIN_ACCENT} />
                <div className="mt-2.5">
                  <QuickChips options={WIN_QUICK} value={winRate} onChange={setWinRate} format={(v) => `${v}%`} />
                </div>
              </div>

              <div>
                <Label hint={`1:${formatVN(rewardRatio, 1, 1)}`}>Tỷ lệ lời/lỗ (Risk-Reward)</Label>
                <NumberInput value={rewardRatio} onChange={setRewardRatio} min={0.1} step={0.1} />
                <div className="mt-2.5">
                  <QuickChips options={RR_QUICK} value={rewardRatio} onChange={setRewardRatio} format={(v) => `1:${formatVN(v, 1, 1)}`} />
                </div>
              </div>

              <div>
                <Label hint={`${formatVN(riskPerTrade, 1, 1)}%`}>Rủi ro mỗi lệnh</Label>
                <Slider value={riskPerTrade} onChange={setRiskPerTrade} min={0.5} max={5} step={0.5} accent={RUIN_ACCENT} />
                <div className="mt-2.5">
                  <QuickChips options={RISK_QUICK} value={riskPerTrade} onChange={setRiskPerTrade} format={(v) => `${formatVN(v, 1, 1)}%`} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: result */}
        <div className="lg:col-span-2">
          <div className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-amber-100 bg-gradient-to-br from-amber-50/70 to-white p-6 md:p-7">
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-amber-200/40 blur-3xl" />

            <div className="relative">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-500">Xác suất phá sản</p>
                <span className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold" style={{ backgroundColor: `${levelColor}1f`, color: levelColor }}>
                  <i className="ri-alert-line" />
                  {level}
                </span>
              </div>

              <div className="mt-6 text-center">
                <p className="font-mono text-5xl font-bold leading-none tabular-nums md:text-6xl" style={{ color: levelColor }}>
                  {displayRuin}%
                </p>
                <p className="mt-3 text-sm text-slate-500">nguy cơ tài khoản về 0 trong dài hạn</p>
              </div>

              <div className="mt-7 grid grid-cols-2 gap-2.5">
                <Stat label="Kỳ vọng mỗi lệnh" value={formatSigned(edge, 'R', 2)} accent={edge >= 0 ? '#10b981' : '#f43f5e'} />
                <Stat label="Đơn vị rủi ro" value={formatVN(units, 0, 0)} />
                <Stat label="Tỷ lệ thắng" value={`${formatVN(winRate, 0, 0)}%`} />
                <Stat label="Tỷ lệ lời/lỗ" value={`1:${formatVN(rewardRatio, 1, 1)}`} />
              </div>

              <p className="mt-6 rounded-xl bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-500">
                Kỳ vọng mỗi lệnh hiện tại đang{' '}
                <strong className="font-semibold" style={{ color: edge >= 0 ? '#059669' : '#e11d48' }}>
                  {edge >= 0 ? 'dương' : 'âm'}
                </strong>
                {edge <= 0
                  ? '. Với kỳ vọng không dương, xác suất phá sản sẽ tiến tới 100% trong dài hạn.'
                  : '. Kỳ vọng dương giúp giảm nguy cơ phá sản khi bạn giữ rủi ro mỗi lệnh ở mức thấp.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* When to use */}
      <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
        <h3 className="font-display text-lg font-bold text-slate-900">Ứng dụng chính</h3>
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
          {WHEN_CARDS.map((c) => (
            <div key={c.title} className="rounded-2xl border border-slate-200/80 bg-slate-50/60 p-5">
              <span className="block h-1 w-8 rounded-full bg-amber-500" />
              <p className="mt-4 text-sm font-bold text-slate-800">{c.title}</p>
              <p className="mt-2 text-xs leading-relaxed text-slate-500">{c.desc}</p>
              <div className="mt-4 rounded-xl border border-slate-200/70 bg-white/80 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-600">Ví dụ</p>
                <p className="mt-1 font-mono text-[11px] leading-relaxed text-slate-600">{c.example}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <NextStep
        title="Bạn đã nắm được nguy cơ phá sản của chiến lược."
        desc="Bạn có thể quay lại tính rủi ro cho từng giao dịch cụ thể."
        cta="Máy tính rủi ro"
        onClick={onNext}
      />
    </div>
  );
}